import { TensileRequirementByGrade } from "@/pages/configuration/models/TensileRequirementByGrade";
import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { Campaign } from "../models/Campaign";
import {
	CampaignDelayResponse,
	CampaignResponse,
} from "../models/CampaignReponse";
import { HeatMechanicalResult } from "../models/HeatMechanicalResult";
import { MechanicalTestRunDisposition } from "../models/MechanicalTestRunDisposition";

const moment = getMomentTz();

type GetCampaignsParams = {
	campaignId?: number | null;
	start?: Date | null;
	end?: Date | null;
};

export const getCampaigns = async (
	params?: GetCampaignsParams,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	if (params?.campaignId)
		parameters.push({ name: "CampaignId", value: params.campaignId });
	if (params?.start)
		parameters.push({ name: "Start", value: params.start.toISOString() });
	if (params?.end)
		parameters.push({ name: "End", value: params.end.toISOString() });

	const resp = await apiService.callV2(
		"[M2].[GetCampaigns]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((campaign: CampaignResponse) => ({
			...campaign,
			Start: campaign.Start ? moment.utc(campaign["Start"]).toDate() : null,
			End: campaign.End ? moment.utc(campaign["End"]).toDate() : null,
			SampleStatusTimestamp: campaign.SampleStatusTimestamp
				? moment.utc(campaign["SampleStatusTimestamp"]).toDate()
				: null,
		}));

		return rows as CampaignResponse[];
	} else {
		throw new Error(resp.message || "Error fetching campaign");
	}
};

export const getCampaignDelays = async (
	params: {
		start?: Date;
		end?: Date;
	},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "Start", value: params.start?.toISOString() ?? null },
		{ name: "End", value: params.end?.toISOString() ?? null },
	];

	const resp = await apiService.callV2(
		"[M2].[GetCampaignDelays]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as CampaignDelayResponse[];
	} else {
		throw new Error(resp.message || "Error fetching campaign delays");
	}
};

export const upsertCampaign = async (campaign: Campaign) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "CampaignId", value: campaign.CampaignId },
		{ name: "CampaignNumber", value: campaign.CampaignNumber },
		{ name: "SectionId", value: campaign.SectionId },
		{ name: "Start", value: campaign.Start.toISOString() },
		{ name: "End", value: campaign.End?.toISOString() ?? null },
		{ name: "ChargedBillets", value: campaign.ChargedBillets },
		{ name: "ActualColdTons", value: campaign.RolledColdTons },
		{ name: "ActualHotTons", value: campaign.RolledHotTons },
	];

	const resp = await apiService.callV2("[M2].[UpsertCampaign]", parameters);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error upserting campaign");
	}
};

export const deleteCampaign = async (campaignId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "CampaignId", value: campaignId }];

	const resp = await apiService.callV2("[M2].[DeleteCampaign]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting campaign");
	}
};

export const upsertMechanicalTestRunDisposition = async ({
	mechanicalTestRunDispositionId,
	mechanicalTestRunId,
	heatId,
	value,
	comment = null,
}: {
	mechanicalTestRunDispositionId?: number;
	mechanicalTestRunId?: number;
	heatId?: number;
	value: "P" | "R" | "U";
	comment?: string | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "Disposition", value },
		{ name: "Comment", value: comment },
	];

	if (mechanicalTestRunDispositionId != null)
		parameters.push({
			name: "MechanicalTestRunDispositionId",
			value: mechanicalTestRunDispositionId,
		});
	else if (mechanicalTestRunId != null)
		parameters.push({
			name: "MechanicalTestRunId",
			value: mechanicalTestRunId,
		});
	else parameters.push({ name: "HeatId", value: heatId ?? null });

	const resp = await apiService.callV2(
		"[LAB].[UpsertMechanicalTestRunDisposition]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error saving the disposition");
	}
};

export type MechanicalResultUpdate = {
	HeatMechanicalResultId: number;
	SampleSequence: number;
	YieldValue: number | null;
	TensileValue: number | null;
	ElongationValue: number | null;
	PercentRAValue: number | null;
};

export const updateHeatMechanicalResult = async (params: {
	mechanicalTestRunId: number;
	results: MechanicalResultUpdate[];
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "MechanicalTestRunId", value: params.mechanicalTestRunId },
		{ name: "Results", value: JSON.stringify(params.results) },
	];

	const resp = await apiService.callV2(
		"[LAB].[UpdateHeatMechanicalResult]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error updating mechanical results");
	}
};

export const getHeatMechanicalResults = async (
	params: { heatId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "HeatId", value: params.heatId }];

	const resp = await apiService.callV2(
		"[LAB].[GetHeatMechanicalResults]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []);

		const results = rows.map((row: HeatMechanicalResult) => ({
			...row,
			Reception: row.Reception ? moment.utc(row.Reception).toDate() : null,
			Reported: row.Reported ? moment.utc(row.Reported).toDate() : null,
			UpdatedAt: moment.utc(row.UpdatedAt).toDate(),
		})) as HeatMechanicalResult[];

		// The grade's reference values, resolved by the SP on its own: they are there even when the
		// heat has no specimens. Same row the tensile requirements screen reads, hence the same
		// model -- and, like that screen, EffectiveDate stays the string the server sent, since it
		// is a calendar date and shifting it into a timezone can only move it.
		const requirement = get(
			resp,
			"data.tables[1].rows[0]",
			null,
		) as TensileRequirementByGrade | null;

		return { results, requirement };
	} else {
		throw new Error(resp.message || "Error fetching heat mechanical results");
	}
};

export const getMechanicalResultVersions = async (
	params: { mechanicalTestRunId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "MechanicalTestRunId", value: params.mechanicalTestRunId },
		{ name: "IncludeAllVersions", value: true },
	];

	const resp = await apiService.callV2(
		"[LAB].[GetHeatMechanicalResults]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []);

		return rows.map((row: HeatMechanicalResult) => ({
			...row,
			Reception: row.Reception ? moment.utc(row.Reception).toDate() : null,
			Reported: row.Reported ? moment.utc(row.Reported).toDate() : null,
			UpdatedAt: moment.utc(row.UpdatedAt).toDate(),
		})) as HeatMechanicalResult[];
	} else {
		throw new Error(resp.message || "Error fetching mechanical result history");
	}
};

export const getMechanicalTestRunDispositions = async (
	params: { heatId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "HeatId", value: params.heatId }];

	const resp = await apiService.callV2(
		"[LAB].[GetMechanicalTestRunDispositions]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((row: MechanicalTestRunDisposition) => ({
			...row,
			DisposedAt: row.DisposedAt ? moment.utc(row.DisposedAt).toDate() : null,
			Reception: row.Reception ? moment.utc(row.Reception).toDate() : null,
			Reported: row.Reported ? moment.utc(row.Reported).toDate() : null,
		}));

		return rows as MechanicalTestRunDisposition[];
	} else {
		throw new Error(resp.message || "Error fetching test run dispositions");
	}
};
